import React from "react";

type MaturityLevel = "Minimal" | "Adequate" | "Strong" | "Extensive";

const LEVELS: { label: MaturityLevel; color: string }[] = [
  { label: "Minimal",   color: "#DC2626" },
  { label: "Adequate",  color: "#F59E0B" },
  { label: "Strong",    color: "#16A34A" },
  { label: "Extensive", color: "#2563EB" },
];

type Props = {
  level: MaturityLevel;
  averageScore?: number;
  controlsEvaluated?: number;
  /** When true, hide numeric metrics (avg score, controls). */
  hideMetrics?: boolean;
};

export function CyberResilienceDial({ level, averageScore, controlsEvaluated, hideMetrics }: Props) {
  const activeIndex = LEVELS.findIndex(l => l.label === level);

  // Gauge geometry — continuous semicircle, 180° split into 4 equal 45° segments
  const cx = 120;
  const cy = 110;
  const r = 85;
  const strokeW = 18;
  const segAngle = 45; // degrees per segment

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  // Build arc path from startAngle to endAngle (in degrees, 0° = right, CCW)
  const arcPath = (startDeg: number, endDeg: number) => {
    const x1 = cx + r * Math.cos(toRad(startDeg));
    const y1 = cy - r * Math.sin(toRad(startDeg));
    const x2 = cx + r * Math.cos(toRad(endDeg));
    const y2 = cy - r * Math.sin(toRad(endDeg));
    const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    // Sweep from start to end going CCW in SVG (sweep-flag 0)
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 0 ${x2} ${y2}`;
  };

  // Needle: map index 0-3 to angle on the semicircle
  // Index 0 → center of leftmost segment, index 3 → center of rightmost
  // Segments go left-to-right: seg0 starts at 180°, seg3 ends at 0°
  const needleAngle = 180 - activeIndex * segAngle - segAngle / 2;
  const needleLen = 60;
  const needleTipX = cx + needleLen * Math.cos(toRad(needleAngle));
  const needleTipY = cy - needleLen * Math.sin(toRad(needleAngle));

  // Small triangle base for the needle
  const baseSize = 6;
  const perpAngle = needleAngle + 90;
  const bx1 = cx + baseSize * Math.cos(toRad(perpAngle));
  const by1 = cy - baseSize * Math.sin(toRad(perpAngle));
  const bx2 = cx - baseSize * Math.cos(toRad(perpAngle));
  const by2 = cy + baseSize * Math.sin(toRad(perpAngle));

  return (
    <div className="flex flex-col items-center gap-2">
      <svg viewBox="0 0 240 130" className="w-72 h-auto" aria-label={`Cyber Resilience: ${level}`}>
        {/* Background track */}
        <path
          d={arcPath(180, 0)}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeW + 4}
          strokeLinecap="butt"
          opacity={0.3}
        />

        {/* Four color segments — drawn as connected arcs with no gap */}
        {LEVELS.map((seg, i) => {
          const startDeg = 180 - i * segAngle;
          const endDeg = startDeg - segAngle;
          const isActive = i === activeIndex;
          return (
            <path
              key={seg.label}
              d={arcPath(startDeg, endDeg)}
              fill="none"
              stroke={seg.color}
              strokeWidth={isActive ? strokeW + 2 : strokeW}
              strokeLinecap="butt"
              opacity={isActive ? 1 : 0.4}
            />
          );
        })}

        {/* Tick marks at segment boundaries */}
        {[0, 1, 2, 3, 4].map(i => {
          const angle = 180 - i * segAngle;
          const innerR = r - strokeW / 2 - 2;
          const outerR = r + strokeW / 2 + 2;
          return (
            <line
              key={`tick-${i}`}
              x1={cx + innerR * Math.cos(toRad(angle))}
              y1={cy - innerR * Math.sin(toRad(angle))}
              x2={cx + outerR * Math.cos(toRad(angle))}
              y2={cy - outerR * Math.sin(toRad(angle))}
              stroke="hsl(var(--background))"
              strokeWidth={2}
            />
          );
        })}

        {/* Needle — triangle pointer */}
        <polygon
          points={`${needleTipX},${needleTipY} ${bx1},${by1} ${bx2},${by2}`}
          fill="hsl(var(--foreground))"
          opacity={0.85}
        />

        {/* Center pivot dot */}
        <circle cx={cx} cy={cy} r={7} fill="hsl(var(--foreground))" />
        <circle cx={cx} cy={cy} r={3} fill="hsl(var(--background))" />
      </svg>

      {/* Segment labels */}
      <div className="flex justify-between w-72 -mt-1 px-1">
        {LEVELS.map((seg, i) => (
          <span
            key={seg.label}
            className={`text-[10px] font-semibold text-center ${
              i === activeIndex ? "" : "text-muted-foreground"
            }`}
            style={i === activeIndex ? { color: seg.color } : undefined}
          >
            {seg.label}
          </span>
        ))}
      </div>

      {/* Active level */}
      <div className="text-center -mt-0.5">
        <p className="text-xs text-muted-foreground">Cyber Resilience Score</p>
        <span
          className="text-xl font-bold tracking-tight uppercase"
          style={{ color: LEVELS[activeIndex]?.color }}
        >
          {level}
        </span>
      </div>

      {/* Supporting metrics */}
      {!hideMetrics && (averageScore != null || controlsEvaluated != null) && (
        <div className="flex gap-6 text-xs text-muted-foreground mt-1">
          {averageScore != null && (
            <span>Average Control Score: <strong className="text-foreground">{averageScore.toFixed(2)}</strong></span>
          )}
          {controlsEvaluated != null && (
            <span>Controls Evaluated: <strong className="text-foreground">{controlsEvaluated}</strong></span>
          )}
        </div>
      )}
    </div>
  );
}
